// ===== FILE: src/utils/urlUtils.ts ===== (UPDATED WITH ADDITIONAL FUNCTIONS)
import { Contractor } from '../models/Contractor.interface';

/**
 * Converts a string to a URL-friendly slug
 * 
 * @param text - The text to convert to a slug
 * @returns URL-safe slug string
 */
export const createSlug = (text: string): string => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')        // Replace spaces with hyphens
    .replace(/[^\w\-]+/g, '')    // Remove all non-word chars
    .replace(/\-\-+/g, '-')      // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '')          // Trim hyphens from start
    .replace(/-+$/, '');         // Trim hyphens from end
};

/**
 * Creates a standardized business name slug from a contractor's business name
 */
export const getBusinessNameSlug = (businessName: string): string => {
  return createSlug(businessName);
};

/**
 * Creates a standardized category slug
 */
export const getCategorySlug = (category: string): string => {
  return createSlug(category);
};

/**
 * Creates a standardized location slug (city or state)
 */
export const getLocationSlug = (location: string): string => {
  return createSlug(location);
};

/**
 * Builds a full SEO-friendly URL for a contractor
 * 
 * @param contractor - The contractor object
 * @param category - Optional category to include in the URL
 * @returns Complete URL path
 */
export const buildContractorUrl = (
  contractor: Contractor, 
  category?: string
): string => {
  if (!contractor) return '/contractors';
  
  // Try to find the most relevant location data
  let state = '';
  let city = '';
  
  if (contractor.searches && contractor.searches.length > 0) {
    // Use the first search location as the primary
    state = getLocationSlug(contractor.searches[0].state);
    city = getLocationSlug(contractor.searches[0].city);
  }
  
  // If no state/city found from searches, try to extract from address
  if (!state && !city && contractor.address) {
    if (typeof contractor.address === 'object') {
      state = getLocationSlug(contractor.address.state || '');
      city = getLocationSlug(contractor.address.city || '');
    }
  }
  
  // Determine which category to use
  let categorySlug = '';
  if (category) {
    categorySlug = getCategorySlug(category);
  } else if (contractor.categories && contractor.categories.length > 0) {
    // Use the first category if not specified
    categorySlug = getCategorySlug(contractor.categories[0]);
  }
  
  // Create the business name slug
  const businessNameSlug = getBusinessNameSlug(contractor.businessName);
  
  // Build the URL based on available parts
  if (state && city && categorySlug) {
    return `/contractors/${state}/${city}/${categorySlug}/${contractor.contractorId}/${businessNameSlug}`;
  } else if (state && city) {
    return `/contractors/${state}/${city}/${contractor.contractorId}/${businessNameSlug}`;
  } else {
    // Fallback to a simpler URL if location data is missing
    return `/contractor-profile/${contractor.contractorId}`;
  }
};

/**
 * Builds a URL for a specific category page
 */
export const buildCategoryUrl = (category: string): string => {
  return `/contractors/${getCategorySlug(category)}`;
};

/**
 * Builds a URL for a state page
 */
export const buildStateUrl = (state: string): string => {
  return `/contractors/${getLocationSlug(state)}`;
};

/**
 * Builds a URL for a city page
 */
export const buildCityUrl = (state: string, city: string): string => {
  return `/contractors/${getLocationSlug(state)}/${getLocationSlug(city)}`;
};

/**
 * Builds a URL for a combined category and location page
 */
export const buildCategoryLocationUrl = (
  state: string, 
  city: string, 
  category: string
): string => {
  return `/contractors/${getLocationSlug(state)}/${getLocationSlug(city)}/${getCategorySlug(category)}`;
};

// NEW FUNCTIONS ADDED FOR MISSING FUNCTIONALITY

/**
 * Convert slug back to display text
 */
export const slugToDisplay = (slug: string): string => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Build search URL with query parameters
 */
export const buildSearchUrl = (searchTerm: string, filters?: any): string => {
  const params = new URLSearchParams();
  params.append('q', searchTerm);
  
  if (filters) {
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
  }
  
  return `/contractors/search?${params.toString()}`;
};